import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

import mammoth from "mammoth";
// import pdf from "pdf-parse";
// import * as path from "path";

import TurndownService from "turndown";

// import { PDFDocument } from "pdf-lib";

const turndownService = new TurndownService();

interface ConversionResult {
  markdown: string;
}

async function convertDocxToMarkdown(file: File): Promise<ConversionResult> {
  const arrayBuffer = await file.arrayBuffer();
  const result = await mammoth.convertToHtml({ arrayBuffer });

  const markdown = turndownService.turndown(result.value);
  return { markdown };
}

import * as pdfjsLib from "pdfjs-dist";
import { Document, Packer, Paragraph } from "docx";
import { TextItem } from "pdfjs-dist/types/src/display/api";
import pdfWorker from "pdfjs-dist/build/pdf.worker?url";
import { DateCategory, SystemMessage } from "@/types/types";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

/**
 * Converts a PDF file buffer to a DOCX Blob (browser-compatible).
 * @param pdfArrayBuffer - The PDF file as an ArrayBuffer.
 * @returns A Promise resolving to a DOCX Blob.
 */
export async function convertPdfToDocx(
  pdfArrayBuffer: ArrayBuffer
): Promise<Blob> {
  try {
    // Convert ArrayBuffer to Uint8Array (pdf.js expects this format)
    const pdfUint8Array = new Uint8Array(pdfArrayBuffer);

    // Load PDF using pdfjs-dist
    const pdf = await pdfjsLib.getDocument({ data: pdfUint8Array }).promise;
    const paragraphs: Paragraph[] = [];

    // Extract text from each page
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item) => (item as TextItem).str)
        .join(" ");
      paragraphs.push(new Paragraph(pageText)); // Add text as a paragraph
    }

    // Create a DOCX document
    const doc = new Document({
      sections: [{ properties: {}, children: paragraphs }],
    });

    // Convert DOCX to Blob (browser-friendly)
    return await Packer.toBlob(doc);
  } catch (error) {
    console.error("Error converting PDF to DOCX:", error);
    throw new Error("Failed to convert PDF to DOCX");
  }
}

const convertPdfToMarkdown = async (file: File) => {
  try {
    console.log("Converting PDF to DOCX...");

    const pdfBuffer = await file.arrayBuffer();
    const docxBlob = await convertPdfToDocx(pdfBuffer); // Now returns a Blob
    console.log("Conversion to DOCX completed");

    const docxFile = new File([docxBlob], "converted.docx", {
      type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    });

    return await convertDocxToMarkdown(docxFile);
  } catch (error) {
    console.error("❌ Error during conversion:", error);
    throw error;
  }
};

async function convertTextToMarkdown(file: File): Promise<ConversionResult> {
  const text = await file.text();
  return {
    markdown: text,
  };
}

async function convertFileToMarkdown({
  file,
}: {
  file: File;
}): Promise<ConversionResult> {
  const extension = file.name.split(".").pop()?.toLowerCase();

  switch (extension) {
    case "docx":
      return convertDocxToMarkdown(file);
    case "pdf":
      return convertPdfToMarkdown(file);
    case "txt":
      return convertTextToMarkdown(file);
    default:
      throw new Error(`Unsupported file type: ${extension}`);
  }
}

export { convertFileToMarkdown };

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
};

export const truncateLink = (link: string) => {
  try {
    const url = new URL(link);
    let display = url.hostname;

    // Add a snippet of the path if it exists
    if (url.pathname && url.pathname !== "/") {
      const pathParts = url.pathname.split("/").filter((part) => part);
      if (pathParts.length > 0) {
        display += `/${pathParts[0]}${pathParts.length > 1 ? "..." : ""}`;
      }
    }

    // Truncate to max 15 characters
    return display.length > 25 ? display.slice(0, 20) + "..." : display;
  } catch {
    // fallback for invalid URLs
    return link.length > 15 ? link.slice(0, 12) + "..." : link;
  }
};

// Function to get enhanced system messages
export const getEnhancedSystemMessage = (state: SystemMessage): string => {
  switch (state) {
    case "data_processing":
      return "PROCESSING DATA";
    case "thinking":
      return "ANALYZING REQUEST";
    case "auto_checking":
      return "VERIFYING RESULTS";
    default:
      return "WORKING";
  }
};

export const getCoolSystemMessage = (state: SystemMessage | null): string => {
  if (!state) return "";
  const messages = {
    data_processing: ["Analyzing Information"],
    thinking: ["Deep thinking"],
    auto_checking: ["Verifying Accurracy"],
    reflecting: [
      "REFLECTING ON RESULTS",
      "CONTEMPLATING FINDINGS",
      "CONSIDERING IMPLICATIONS",
      "SYNTHESIZING INSIGHTS",
    ],
    writing: [],
    writing_section_1: ["WRITING SECTION 1"],
    auto_checking_proposing: ["CHECKING PROPOSE SECTION"],
    auto_checking_verifying: ["VERIFYING PROPOSED SECTION"],
    auto_checking_applying: ["APPLYING PROPOSE SECTION"],
    saving: ["SAVING"],
    save_success: ["SAVED"],
  };

  const options = messages[state];
  const randomIndex = Math.floor(Math.random() * options.length);
  return options[randomIndex];
};

export const categorizeByDate = (
  date: string | Date | undefined
): DateCategory => {
  if (!date) return "Reminder";

  const now = new Date();
  const createdAt = new Date(date);

  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfCreated = new Date(
    createdAt.getFullYear(),
    createdAt.getMonth(),
    createdAt.getDate()
  );

  const diffMs = startOfToday.getTime() - startOfCreated.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays === 2) {
    return "2 days ago";
  } else if (diffDays > 2 && diffDays <= 7) {
    return "Last week";
  } else {
    return "Reminder";
  }
};

export const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export function stripMarkdown(markdownText: string) {
  return (
    markdownText
      // Remove headings (e.g. # Title)
      ?.replace(/^#+\s*/gm, "")
      // Remove bold/italic/inline code (e.g. **bold**, *italic*, `code`)
      ?.replace(/(\*\*|__)(.*?)\1/g, "$2")
      ?.replace(/(\*|_)(.*?)\1/g, "$2")
      ?.replace(/`([^`]+)`/g, "$1")
      // Remove list markers (e.g. - item)
      ?.replace(/^\s*[-+*]\s+/gm, "")
      // Remove remaining blockquote markers (e.g. > quote)
      ?.replace(/^\s*>+\s?/gm, "")
      // Trim excess whitespace
      ?.replace(/\n{2,}/g, "\n\n")
      ?.trim()
  );
}

export function countWordsMarkdown(markdownText: string | null) {
  if (!markdownText) return 0;
  // Remove Markdown syntax elements
  const textContent = markdownText.replace(/([*_#`~\-\[\]\(\)])/g, "");

  // Remove HTML tags
  const noHTML = textContent.replace(/<[^>]*>/g, "");

  // Split the string into words, handling multiple spaces
  const words = noHTML.split(/\s+/).filter((word) => word !== "");

  // Return the number of words
  return words.length;
}
